module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('h2', {
    staticClass: "h2a",
    attrs: {
      "id": "ch03"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_19"
    }
  }, [_vm._v("Genetics Provides a Window on Human Individuality")])]), _c('a', {
    attrs: {
      "id": "page_19"
    }
  }, [_c('p', {
    staticClass: "ch-author"
  }, [_vm._v("Jeremy Nathans")]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_c('span', {
    staticClass: "small"
  }, [_vm._v("ANYONE WHO HAS SPENT TIME")]), _vm._v(" in a room full of four-year olds has seen the evidence. Even at a young age, we humans show striking personality differences. Some children are outgoing; others are shy. Some children are focused; others jump from one activity to another. Some children are strong-willed; others, less so. Personality traits largely define who we are as adults—pessimistic or optimistic, sociable or solitary, authoritarian or free-spirited, empathetic or suspicious. Aggregated over thousands or millions of people, such traits define the characteristics of our societies.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("What determines personality? To what extent is it innate? To what extent is it molded by experience? At their core, these are questions about brain development, function, and plasticity, and they are some of the deepest questions that we can ask of brain science.")])]), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_19"
    }
  }, [_vm._v("More than one hundred years ago, the British polymath Francis Galton posed these questions in essentially their modern form."), _c('sup')]), _c('a', {
    attrs: {
      "id": "ch03fn1a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn1"
    }
  }, [_vm._v("1")]), _vm._v(" Galton conceptualized the forces that mold personality, intelligence, and other mental characteristics as a reflection of the combined contributions of “nature and nurture.” Over the past century, research in animal behavior, psychology, and genetics has begun to converge and to focus this inquiry.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In a consideration of the lessons that we might glean from our nonhuman relatives, there is no better place to start than with the work of Galton’s cousin, Charles Darwin, who was fascinated by the changes in physical appearance and behavior that could be elicited by the selective breeding of domesticated animals. Consider, for example, the personalities of dogs. As every dog owner knows, individual dogs have distinctive temperaments, skills (or lack thereof), and habits—in short, a set of traits that defines the dog’s personality. Strikingly, these attributes have a strong genetic component. A golden retriever’s warm personality, an Australian sheepdog’s herding instinct, and a German shepherd’s self-discipline are, in large part, the product of selective breeding. To dog owners and breeders, these behavioral traits are as valued and as distinctive as the dogs’ physical features.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_20"
    }
  }, [_vm._v("If we examine the broadest of canine behavioral traits—those that distinguish wild and domesticated dogs—we observe that the critical characteristic shared by all domesticated breeds is tameness, a fundamental change in the ground rules of interpersonal interactions with humans. This trait is exemplified by a dramatic change in the meaning of eye contact, from threat to affection. In a landmark study of Siberian silver foxes conducted by Dmitri Balyaev, Lyudmila Trut, and their colleagues, the behavioral transition from a wild to a tame temperament was achieved with only 30–40 generations of selective breeding of wild foxes."), _c('sup')]), _c('a', {
    attrs: {
      "id": "ch03fn2a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn2"
    }
  }, [_vm._v("2")]), _vm._v(" This breeding program, carried out in Novosibirsk starting in the late 1950s, ultimately produced foxes that exhibited many of the endearing traits that we associate with domesticated dogs, including tail wagging, hand licking, responding to human calls, and a desire for physical and eye contact with humans.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("One of the lessons from the Novosibirsk study is that the underlying genetic variation required for the transition from a wild to a tame temperament preexisted in the wild fox population. Indeed, the researchers observed that “friendly” behavior began to emerge after only four generations of breeding (“friendliness” being defined by the interactions between foxes and humans). At present, the precise genetic changes responsible for tameness in Siberian foxes are not known, but Balyaev, Trut, and their colleagues have presented evidence that—whatever those changes are—they lead to hormonal changes that include a lowering of levels of stress hormones, such as glucocorticoids. Perhaps a “type A” personality is optimally suited to a world in which the next meal is unpredictable and every large animal is a likely adversary.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_21"
    }
  }, [_vm._v("To what extent do these insights into the genetic control of behavioral traits in animals apply to us? In 1979, Thomas Bouchard, a psychologist at the University of Minnesota, launched one of the most ambitious attempts to answer this question. Over the next twenty years, Bouchard and his colleagues searched for those rare twins who had been adopted into different households and raised separately to determine the extent to which their psychological similarities and differences reflected shared genetics or different environments."), _c('sup')]), _c('a', {
    attrs: {
      "id": "ch03fn3a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn3"
    }
  }, [_vm._v("3")]), _vm._v(" The Minnesota Study of Twins Reared Apart (MISTRA) compared identical twins and fraternal twins, and, in collaboration with Bouchard’s University of Minnesota colleague David Lykken, it also compared twins reared apart with twins reared together."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch03fn4a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn4"
    }
  }, [_vm._v("4")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Identical twins (also called monozygous twins) arise from a single fertilized egg that, early in development, divides to form two embryos. The two individuals inherit the same version of each gene from their parents and are, therefore, genetically identical. Since identical twin embryos also inherit the same arrangement of X- and Y-chromosomes, they are also the same sex. That is, identical twin pairs can consist of two boys or two girls but never a boy and a girl. Approximately 1 out of every 270 humans is a member of an identical twin pair.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In contrast, fraternal twins (also called dizygous twins) arise when two eggs are released during the same ovulatory cycle, are independently fertilized by two sperm, and then develop into two embryos. The two individuals are only as similar to each other as are any other pair of siblings. The distinguishing feature of fraternal twin siblings, as compared to other siblings, is that these siblings share the same uterus and are the same age. Geneticists loosely say (or write) that “fraternal twins share, on average, 50 percent of their genes.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "ch03fn5a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn5"
    }
  }, [_vm._v("5")])]), _vm._v(" Similarly, since fraternal twin embryos have independently inherited their X- and Y-chromosomes, they are as likely to be the same sex (boy + boy or girl + girl) as opposite sex (boy + girl or girl + boy). Approximately 1 out of every 115 humans is a member of a fraternal twin pair.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The simplest twin study design is one in which the values for some quantifiable trait—for example, height, weight, or blood pressure—are determined for both members of a large number of fraternal and identical twin pairs. The differences in these values are calculated for each pair, and the results are compared between fraternal and identical groups. Since identical twins are always of the same sex, the study design limits the participating fraternal twins to those that are also of the same sex. One study of this type has shown, for example, that the average height difference between fraternal twins is approximately 4.5 centimeters, whereas the average difference between identical twins is approximately 1.7 centimeters. The smaller average difference between identical twins is attributed to their greater degree of genetic similarity.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_22"
    }
  }, [_vm._v("The alert reader may have discerned a potential fly in the ointment for this type of study, especially as applied to traits with a psychological component. Identical twins often look so similar that they are confused with one another—an occasional source of amusement. As a result, they may find that their teachers, friends, or even relatives tend to treat them in similar ways, either because they cannot tell the twins apart or because they subconsciously assume that two people who look so much alike are also similar in other respects. A similarity in interpersonal interactions of this type creates what behavioral geneticists call a “shared environment,” and it can confound the analysis of nature versus nurture. Additionally, as described below, identical twins do, in fact, tend to resemble one another on a wide range of personality traits and, perhaps in consequence, often develop an extraordinarily close bond with one another. This development leads to a second conundrum: maybe the close interpersonal relationship between many identical twins tends to reinforce their psychological similarities and suppress their differences.")])]), _c('a', {
    attrs: {
      "id": "page_22"
    }
  }, [_c('p', {
    staticClass: "indent"
  }, [_vm._v("Studying twins reared apart from birth or infancy neatly solves such problems. As MISTRA showed, a comparison between identical twins reared apart and fraternal twins reared apart is particularly informative. In this comparison, the twin pairs are either 100 percent genetically identical or on average 50 percent genetically identical, respectively, but their rearing environments are largely uncorrelated. Two other useful comparisons are between identical twins reared together versus apart and between fraternal twins reared together versus apart. The latter comparisons provide another approach to assessing the influence of shared versus unshared environment during the formative years of childhood.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Over its twenty-year life, the MISTRA scientists studied eighty-one pairs of identical twins raised apart and fifty-six pairs of fraternal twins raised apart. At the time that they were studied, the twins averaged forty-one years of age. They had spent an average of only five months together before being separated and then had no contact with one another for an average of thirty years. During the study, each participant typically spent a week at the University of Minnesota and underwent a comprehensive set of physical, medical, and psychological tests.")])]), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_22"
    }
  }), _c('a', {
    attrs: {
      "id": "page_23"
    }
  }, [_vm._v("The results from the psychological tests were striking. Across a range of personality features, such as extraversion/introversion and emotional lability/stability, genetic influences were substantial, averaging roughly 40 percent of the variance, a statistical measure of the variation across the population being studied. Moreover, vocational interests and specific social behaviors, such as religiosity and traditionalism, showed a similarly large genetic influence.")])]), _c('a', {
    attrs: {
      "id": "page_23"
    }
  }), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_23"
    }
  }, [_vm._v("The single most intensively studied of all human psychological traits is the Intelligence Quotient (IQ), which is determined by performance on a written test of knowledge and intellectual skill. Although the name “IQ” is unduly grandiose, the test is of both theoretical and practical interest as its results are strongly predictive of educational and vocational success."), _c('sup')]), _c('a', {
    attrs: {
      "id": "ch03fn6a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn6"
    }
  }, [_vm._v("6")]), _vm._v(" The data from MISTRA showed that for the population studied, about 70 percent of the variance in IQ test scores could be explained by genetics. In particular, the difference in IQ test scores between identical twins raised apart was only slightly greater than the average difference between two test scores obtained when the same person took the test on two separate occasions. A systematic analysis of the different adopted households in which the identical twins were raised showed little influence on IQ test scores of parental educational level or household cultural or scientific enrichment. These results are remarkable, but they need a few qualifiers because they do not address the influence of extremes of environmental enrichment or deprivation. Nearly all of the MISTRA participants were raised in households and in communities that provided opportunities for a solid education, and therefore the strength of the genetic influence applies to that broadly permissive environment.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("I have emphasized MISTRA because of its large size and exemplary design, but many dozens of other twin and family studies provide data on personality and IQ that closely agree with the results from MISTRA."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch03fn7a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn7"
    }
  }, [_vm._v("7")])]), _vm._v(" An especially intriguing comparison of 110 identical twin pairs and 130 fraternal twin pairs who were all over eighty years old found a higher degree of similarity for identical twins compared to fraternal twins on every measure tested, including general cognitive ability (IQ), memory, verbal ability, spatial ability, and processing speed."), _c('sup', [_c('a', {
    attrs: {
      "id": "ch03fn8a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn8"
    }
  }, [_vm._v("8")])]), _vm._v(" This study also showed that the genetic influence on IQ does not decline appreciably with age, although this and other studies do not address the extent to which a person’s IQ score reflects motivation, curiosity, and self-discipline, in addition to “intelligence” per se.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_24"
    }
  }, [_vm._v("Twin studies can measure the average contribution of genetics to individual variation in personality and cognitive ability, but they cannot reveal the biological mechanisms responsible for this variation. We can think of twin studies as providing us with data that are analogous to the performance characteristics of different types of automobiles. We may learn that a Porsche accelerates more rapidly than a Toyota, but to understand the cause of that difference we will need to know in detail how these two automobiles differ. We will also need to know a lot about how automobiles work in general.")])]), _c('a', {
    attrs: {
      "id": "page_24"
    }
  }, [_c('p', {
    staticClass: "indent"
  }, [_vm._v("In biology, looking under the hood means understanding how cells grow, become specialized, interact, and carry out their particular functions. It also means understanding how the genetic blueprint that each of us inherits codes for the proteins that comprise the molecular machines that underpin all cellular structure and function. This is a tall order, and we are still far from having a fully satisfactory understanding of these processes. However, progress over the past fifty years has been impressive. The fundamental mechanisms underlying communication between nerve cells are now known, as are many of the mechanisms responsible for assembling connections between nerves cells during development.")])]), _c('p', {
    staticClass: "indent"
  }, [_c('a', {
    attrs: {
      "id": "page_24"
    }
  }, [_vm._v("Progress in genetics has been especially rapid. We now have the complete DNA sequences of our species and many dozens of other species, and partial DNA sequences have been determined from hundreds of thousands of individual humans. These sequences show that our genetic blueprint is remarkably similar to the genetic blueprints of other mammals. Thus the large physical and mental differences among different mammalian species likely arise from the sum of many relatively subtle differences in gene structure and function. Additionally, a comparison of the DNA blueprints from different humans shows that we differ genetically from one another, on average, by only one part in one thousand."), _c('sup')]), _c('a', {
    attrs: {
      "id": "ch03fn9a"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn9"
    }
  }, [_vm._v("9")]), _vm._v(" Determining how these genetic differences contribute to making each of us who we are is one of mankind’s greatest scientific challenges.")]), _vm._v(" "), _c('h3', {
    staticClass: "h3"
  }, [_c('a', {
    attrs: {
      "id": "page_25"
    }
  }, [_c('span', {
    staticClass: "small"
  }, [_vm._v("NOTES")])])]), _c('a', {
    attrs: {
      "id": "page_25"
    }
  }), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "page_25"
    }
  }), _c('a', {
    attrs: {
      "id": "ch03fn1"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn1a"
    }
  }, [_vm._v("1")]), _vm._v(". F. Galton, "), _c('em', [_vm._v("Inquiries into Human Faculty and Its Development")]), _vm._v(" (London: J. M. Dart, 1907), available at "), _c('a', {
    attrs: {
      "href": "http://www.galton.org",
      "target": "_blank"
    }
  }, [_vm._v("www.galton.org")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "ch03fn2"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn2a"
    }
  }, [_vm._v("2")]), _vm._v(". D. K. Balyaev, “Destabilizing Selection as a Factor in Domestication,” "), _c('em', [_vm._v("Journal of Heredity")]), _vm._v(" 70 (1979): 301–308; L. Trut, “Early Canid Domestication: The FarmFox Experiment,” "), _c('em', [_vm._v("American Scientist")]), _vm._v(" 87 (1999): 160–169.")]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "ch03fn3"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn3a"
    }
  }, [_vm._v("3")]), _vm._v(". T. J. Bouchard, D. T. Lykken, M. McGue, N. L. Segal, and A. Tellegen, “Sources of Human Psychological Differences: The Minnesota Study of Twins Reared Apart,” "), _c('em', [_vm._v("Science")]), _vm._v(" 250 (1990): 223–228.")]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "ch03fn4"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn4a"
    }
  }, [_vm._v("4")]), _vm._v(". N. L. Segal, "), _c('em', [_vm._v("Born Together—Reared Apart: The Landmark Minnesota Twin Study")]), _vm._v(" (Cambridge, MA: Harvard University Press, 2012).")]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "ch03fn5"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn5a"
    }
  }, [_vm._v("5")]), _vm._v(". The statement “fraternal twins share, on average, 50 percent of their genes” is really a compressed version of the more precise statement that “fraternal twins have a 50 percent chance of inheriting the same version of any particular gene from each parent.” For example, if a mother has two different versions of a gene for eye color, then each of her children has a 50 percent chance of inheriting version one and a 50 percent chance of inheriting version two. From this statement, the reader can easily write down all of the possible combinations of inherited versions and see that, on average, fraternal twins inherit the same version half the time and different versions the other half of the time.")]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "ch03fn6"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn6a"
    }
  }, [_vm._v("6")]), _vm._v(". It is important to emphasize that, like all standardized testing, the results of IQ testing should be interpreted in a cultural context. Assuming that we could agree that “innate” intelligence exists and can be measured, it is unlikely that any standardized test could measure it in a manner that is free of cultural bias.")]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "ch03fn7"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn7a"
    }
  }, [_vm._v("7")]), _vm._v(". L. J. Eaves, H. J. Eysenck, and N. G. Martin, "), _c('em', [_vm._v("Genes, Culture, and Identity: An Empirical Approach")]), _vm._v(" (New York: Academic Press, 1989).")]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "ch03fn8"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn8a"
    }
  }, [_vm._v("8")]), _vm._v(". G. E. McLearn, B. Johnasson, S. Berg, N. L. Pederson, F. Ahern, S. A. Petrill, and R. Plomin, “Substantial Genetic Influence on Cognitive Abilities in Twins 80 or More Years Old,” "), _c('em', [_vm._v("Science")]), _vm._v(" 276 (1997): 1560–1563.")]), _vm._v(" "), _c('p', {
    staticClass: "notes"
  }, [_c('a', {
    attrs: {
      "id": "ch03fn9"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#ch03fn9a"
    }
  }, [_vm._v("9")]), _vm._v(". E. S. Lander, “Initial Impact of the Sequencing of the Human Genome,” "), _c('em', [_vm._v("Nature")]), _vm._v(" 470 (2011): 187–197.")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-c18b27f4","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/xhtml/ch03.vue
// module id = 84
// module chunks = 0